import { API_ENDPOINTS } from '../../../../platform/app/src/custom/config/api.config';
import { authService } from '../../../../platform/app/src/custom/auth/authService';
import { isImportedStudyUID } from '../ViewerLayout/ImportStudy/importedStudyApi';

export type StudyReportInfo = {
  hasReport: boolean;
  status: string | null;
  isApproved: boolean;
};

export function normalizeReportStatusFromPayload(
  data: Record<string, unknown> | null
): string | null {
  if (!data) {
    return null;
  }
  const status = data.status ?? data.report_status;
  if (status == null || status === '') {
    return null;
  }
  return String(status).trim();
}

export function isApprovedReportStatus(status: string | null): boolean {
  if (!status) {
    return false;
  }
  const lower = status.toLowerCase();
  return lower === 'approved' || lower === 'approve';
}

export function isPendingReportStatus(status: string | null): boolean {
  if (!status) {
    return false;
  }
  return status.toLowerCase().trim() === 'pending';
}

/** Report exists and may be opened from the viewer header (approved or pending). */
export function isViewableReportStatus(status: string | null): boolean {
  return isApprovedReportStatus(status) || isPendingReportStatus(status);
}

export async function fetchStudyReportInfo(
  studyInstanceUID: string,
  extensionManager?: { getActiveDataSource: () => unknown[] | unknown },
  options?: {
    treatQidoApprovedAsReport?: boolean;
  }
): Promise<StudyReportInfo> {
  if (!studyInstanceUID) {
    return { hasReport: false, status: null, isApproved: false };
  }

  if (isImportedStudyUID(studyInstanceUID)) {
    return { hasReport: false, status: null, isApproved: false };
  }

  let hasRisRow = false;
  let status: string | null = null;
  let qidoStatus: string | null = null;

  try {
    const accessToken = authService.getAccessToken();
    const response = await fetch(API_ENDPOINTS.REPORTS.GET_BY_STUDY_UID(studyInstanceUID), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    });

    if (response.ok) {
      const apiResponse = await response.json();
      if (apiResponse.success && apiResponse.data != null) {
        hasRisRow = true;
        status = normalizeReportStatusFromPayload(apiResponse.data as Record<string, unknown>);
      }
    }

    if (extensionManager?.getActiveDataSource) {
      try {
        const dsResult = extensionManager.getActiveDataSource();
        const ds = Array.isArray(dsResult) ? dsResult[0] : dsResult;
        const search = (ds as { query?: { studies?: { search?: Function } } })?.query?.studies
          ?.search;
        if (typeof search === 'function') {
          const studies = await search.call((ds as { query: { studies: unknown } }).query.studies, {
            studyInstanceUid: studyInstanceUID,
            limit: 1,
          });
          const st = studies?.[0] as { reportStatus?: string } | undefined;
          if (st?.reportStatus) {
            qidoStatus = String(st.reportStatus).trim();
          }
        }
      } catch (error) {
        console.warn('fetchStudyReportInfo: QIDO study lookup failed:', error);
      }
    }

    let effectiveStatus = status;
    let showHasReport = hasRisRow;

    if (hasRisRow) {
      if (!effectiveStatus && qidoStatus) {
        effectiveStatus = qidoStatus;
      }
    } else if (qidoStatus && options?.treatQidoApprovedAsReport) {
      effectiveStatus = qidoStatus;
      if (isViewableReportStatus(qidoStatus)) {
        showHasReport = true;
      }
    }

    return {
      hasReport: showHasReport,
      status: effectiveStatus,
      isApproved: isApprovedReportStatus(effectiveStatus),
    };
  } catch (error) {
    console.warn('fetchStudyReportInfo failed:', error);
    return { hasReport: false, status: null, isApproved: false };
  }
}
